// features/landing/components/WhatsNewDialog.tsx
//
// "What's new" changelog for everyone (read-only; admins POST from the Admin
// Dashboard). Merges implementedFeatures (manual patch notes + shipped
// features) and fixedBugs into one chronological feed.
//
// Two ways it opens:
//  - manually, from the nav footer (parent bumps `requestOpen`);
//  - automatically once after login when there's a post newer than the
//    user's last-seen marker (users/{uid}/lastSeenWhatsNew). Closing it
//    stamps the marker so they aren't prompted again until a NEW post lands.
//    A per-session sessionStorage guard stops it re-opening on every page.
//
// Rendered git-style: entries are monospace and there's a Copy button, so the
// whole changelog can be select-and-pasted.

import { ReactElement, useEffect, useState, useCallback, useRef } from 'react';
import { Dialog, DialogContent, Box, Typography, Chip, IconButton, Tooltip, Grow } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CampaignIcon from '@mui/icons-material/Campaign';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ref, query, limitToLast, get, update, remove } from 'firebase/database';
import { db } from '../../../config/firebase';

// What's New is admin-authored patch notes only. Auto-tracked shipped
// features and bug fixes (implementedFeatures with isManualUpdate falsey,
// and the whole fixedBugs tree) are intentionally excluded — they're noise
// for end users. The single remaining kind keeps the git-style rendering.
type Kind = 'update';
interface FeedItem { id: string; kind: Kind; text: string; at: number; by?: string }

const META: Record<Kind, { label: string; tag: string; tone: 'primary' | 'success' | 'error' }> = {
  update: { label: 'Update', tag: 'update', tone: 'primary' },
};

const MAX = 25;
// Don't re-query the feed more than once per this window when the tab refocuses.
const RECHECK_THROTTLE_MS = 30_000;
const fmt = (ts: number) => (ts ? new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

interface RawItem {
  title?: string;
  description?: string;
  implementedAt?: number;
  implementedBy?: string;
  isManualUpdate?: boolean;
}

async function loadFeed(): Promise<FeedItem[]> {
  const featSnap = await get(query(ref(db, 'implementedFeatures'), limitToLast(MAX)));
  const feed: FeedItem[] = [];
  Object.entries((featSnap.val() || {}) as Record<string, RawItem>).forEach(([id, f]) => {
    // Only admin-posted patch notes (isManualUpdate). Skip auto-logged features.
    if (!f.isManualUpdate) return;
    feed.push({
      id,
      kind: 'update',
      text: f.description || f.title || '',
      at: f.implementedAt || 0,
      by: f.implementedBy,
    });
  });
  return feed.filter((f) => f.text).sort((a, b) => b.at - a.at);
}

export interface WhatsNewDialogProps {
  /** Bump this number to open the dialog manually (from the nav footer). */
  requestOpen?: number;
  /** Current user — enables seen-tracking + auto-prompt. */
  userUid?: string;
  /** Admins can delete entries straight from the popup. */
  canManage?: boolean;
}

export function WhatsNewDialog({ requestOpen = 0, userUid, canManage = false }: WhatsNewDialogProps): ReactElement | null {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<FeedItem[]>([]);
  const [latestAt, setLatestAt] = useState(0);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchInto = useCallback(async () => {
    setLoading(true);
    try {
      const feed = await loadFeed();
      setItems(feed);
      setLatestAt(feed[0]?.at || 0);
      return feed;
    } catch {
      setItems([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Manual open (footer). Skip the initial render (requestOpen starts at 0).
  useEffect(() => {
    if (requestOpen <= 0) return;
    fetchInto();
    setOpen(true);
  }, [requestOpen, fetchInto]);

  // Auto-prompt when there's an unseen post — on login AND whenever the user
  // brings the page back to life (tab refocus / window focus), so an
  // already-logged-in user still sees a freshly-posted update without a manual
  // refresh. Throttled so rapid focus changes don't re-query every time.
  const lastCheckRef = useRef(0);
  const checkingRef = useRef(false);
  const maybePrompt = useCallback(async () => {
    if (!userUid || open) return;
    const now = Date.now();
    if (now - lastCheckRef.current < RECHECK_THROTTLE_MS) return;
    lastCheckRef.current = now;
    if (checkingRef.current) return;
    checkingRef.current = true;
    try {
      const [feed, seenSnap] = await Promise.all([
        loadFeed(),
        get(ref(db, `users/${userUid}/lastSeenWhatsNew`)),
      ]);
      const latest = feed[0]?.at || 0;
      const lastSeen = (seenSnap.val() as number | null) || 0;
      if (latest > lastSeen) {
        setItems(feed);
        setLatestAt(latest);
        setOpen(true);
      }
    } catch {
      // ignore — manual open still works
    } finally {
      checkingRef.current = false;
    }
  }, [userUid, open]);

  useEffect(() => {
    if (!userUid) return;
    void maybePrompt(); // initial check on mount / login
    const onVisible = () => {
      if (document.visibilityState === 'visible') void maybePrompt();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [userUid, maybePrompt]);

  const handleClose = useCallback(() => {
    setOpen(false);
    // Stamp the seen marker so we don't prompt again until a newer post.
    if (userUid && latestAt) {
      update(ref(db, `users/${userUid}`), { lastSeenWhatsNew: latestAt }).catch(() => undefined);
    }
  }, [userUid, latestAt]);

  const plainText = useCallback(
    () =>
      items
        .map((u) => `* [${META[u.kind].tag}] ${u.text}${u.by ? ` (${fmt(u.at)}, ${u.by})` : fmt(u.at) ? ` (${fmt(u.at)})` : ''}`)
        .join('\n'),
    [items],
  );

  const handleDelete = useCallback(async (id: string) => {
    if (!canManage) return;
    if (typeof window !== 'undefined' && !window.confirm('Remove this entry from What’s new?')) return;
    try {
      await remove(ref(db, `implementedFeatures/${id}`));
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch {
      // best-effort; rule denies non-admins
    }
  }, [canManage]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(plainText());
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard best-effort
    }
  }, [plainText]);

  if (!open) return null;

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Grow}
      transitionDuration={{ enter: 300, exit: 160 }}
      PaperProps={{
        sx: {
          backgroundColor: 'background.default',
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      {/* Hero header — matches the chat widget's calm gradient. */}
      <Box
        sx={{
          position: 'relative',
          px: 3,
          pt: 2.75,
          pb: 2.75,
          color: '#fff',
          background: (t) =>
            `linear-gradient(180deg, ${alpha('#0A0E14', 0.32)}, ${alpha('#0A0E14', 0.55)}), ` +
            `linear-gradient(135deg, ${t.palette.primary.dark}, ${t.palette.primary.main} 62%, ${t.palette.secondary.dark})`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: alpha('#FFFFFF', 0.16), flexShrink: 0 }}>
            <CampaignIcon
              sx={{
                color: '#fff',
                fontSize: 22,
                transformOrigin: '70% 70%',
                animation: 'wnBell 0.9s ease 0.25s 1',
                '@keyframes wnBell': {
                  '0%, 100%': { transform: 'rotate(0deg)' },
                  '20%': { transform: 'rotate(-14deg)' },
                  '40%': { transform: 'rotate(11deg)' },
                  '60%': { transform: 'rotate(-7deg)' },
                  '80%': { transform: 'rotate(4deg)' },
                },
              }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.2 }}>What&apos;s new</Typography>
            <Typography sx={{ fontSize: 12, color: alpha('#FFFFFF', 0.82) }}>The latest RoundMaster updates</Typography>
          </Box>
          {items.length > 0 && (
            <Tooltip title={copied ? 'Copied!' : 'Copy changelog'}>
              <IconButton onClick={handleCopy} size="small" aria-label="Copy changelog" sx={{ color: copied ? '#9CFFE9' : alpha('#FFFFFF', 0.9) }}><ContentCopyIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
          <IconButton onClick={handleClose} size="small" aria-label="Close" sx={{ color: alpha('#FFFFFF', 0.9) }}><CloseIcon /></IconButton>
        </Box>
      </Box>

      {/* Body — one clean card per entry. */}
      <DialogContent sx={{ p: 1.75, backgroundColor: 'background.default' }}>
        {loading ? (
          <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>Loading…</Typography>
        ) : items.length === 0 ? (
          <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>Nothing posted yet.</Typography>
        ) : (
          items.map((u, i) => {
            const m = META[u.kind];
            return (
              <Box
                key={u.id}
                sx={{
                  p: 2,
                  mb: 1.5,
                  borderRadius: 3,
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
                  '&:last-of-type': { mb: 0 },
                  // Gentle staggered fade-up as the popup opens.
                  animation: 'wnCardIn .42s cubic-bezier(.2,.7,.2,1) both',
                  animationDelay: `${Math.min(i, 8) * 70}ms`,
                  '@keyframes wnCardIn': {
                    from: { opacity: 0, transform: 'translateY(10px)' },
                    to: { opacity: 1, transform: 'none' },
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <Chip label={m.label} size="small" sx={{ height: 22, fontSize: '0.66rem', fontWeight: 700, borderRadius: 99, backgroundColor: (t) => alpha(t.palette[m.tone].main, 0.15), color: `${m.tone}.main` }} />
                  <Box sx={{ flex: 1 }} />
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', whiteSpace: 'nowrap' }}>
                    {fmt(u.at)}{u.by ? ` · ${u.by}` : ''}
                  </Typography>
                  {canManage && (
                    <Tooltip title="Remove this entry">
                      <IconButton size="small" onClick={() => handleDelete(u.id)} sx={{ color: 'error.main', p: 0.25 }}>
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Typography sx={{ color: 'text.primary', fontSize: '0.875rem', lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {u.text}
                </Typography>
              </Box>
            );
          })
        )}
      </DialogContent>
    </Dialog>
  );
}

export default WhatsNewDialog;
